import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { doc, setDoc, collection } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { INDUSTRIES } from '@/config/industries';
import type { IndustryId } from '@/config/industries';
export function OwnerCompanyCreatePage() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [industryId, setIndustryId] = useState<IndustryId | ''>('');
  const [country, setCountry] = useState('MX');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!name.trim()) {
      setError('El nombre es obligatorio');
      return;
    }
    if (!industryId || !INDUSTRIES.some((i) => i.id === industryId)) {
      setError('Selecciona un rubro empresarial');
      return;
    }
    setSaving(true);
    try {
      const companyRef = doc(collection(db, 'companies'));
      const companyId = companyRef.id;
      await setDoc(companyRef, {
        name: name.trim(),
        industry: industryId,
        country: country || 'MX',
        branding: {},
        createdAt: new Date().toISOString(),
      });
      navigate(`/owner/companies/${companyId}`, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al crear la empresa');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" asChild>
        <Link to="/owner/companies">← Empresas</Link>
      </Button>
      <Card>
        <CardHeader>
          <CardTitle>Crear empresa</CardTitle>
          <p className="text-sm text-muted-foreground">
            Añade una nueva empresa al sistema. Luego podrás asignar usuarios desde la sección Usuarios.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
            )}
            <div className="space-y-2">
              <Label htmlFor="companyName">Nombre</Label>
              <Input
                id="companyName"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nombre de la empresa"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="companyIndustry">Rubro empresarial</Label>
              <select
                id="companyIndustry"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={industryId}
                onChange={(e) => setIndustryId(e.target.value as IndustryId | '')}
              >
                <option value="">Selecciona un rubro</option>
                {INDUSTRIES.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="companyCountry">País</Label>
              <select
                id="companyCountry"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
              >
                <option value="MX">México</option>
                <option value="PE">Perú</option>
                <option value="CO">Colombia</option>
                <option value="ES">España</option>
                <option value="AR">Argentina</option>
                <option value="CL">Chile</option>
                <option value="US">Estados Unidos</option>
              </select>
            </div>
            <Button type="submit" disabled={saving}>
              {saving ? 'Creando...' : 'Crear empresa'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
